import type {RunOutcome} from './novaInvocationStore';

// Why this task cannot be actioned, as a property of the task and the person looking at it.
//
// 'no_edit_task' covers both halves of "may not act on this task": the person does not hold the
// edit-tasks permission, or they are a client user restricted from task actions. See
// deriveEligibilityFromTask for why those are one reason rather than two.
export type EligibilityReason = 'task_locked' | 'no_edit_task';

// A disabled control can also be blocked by run capacity rather than the task's own eligibility —
// once three tasks are running at once, every other idle control disables itself until one
// finishes. 'run_cap_reached' widens the disabled reason to cover that alongside eligibility.
//
// 'no_agent_available' is the third such reason: nothing about this task, and nothing about this
// person's permissions — there is simply no agent in the resolved list for a run to use. Its own
// member rather than anything phrased about the person, because no agent carries a role
// restriction: attributing an empty list to who the person is would state a cause that does not
// exist.
export type DisabledReason = EligibilityReason | 'run_cap_reached' | 'no_agent_available';

export type ControlState =
	| {kind: 'action'}
	| {kind: 'working'}
	| {kind: 'ready_or_continue'; read: boolean}
	| {kind: 'disabled'; reason: DisabledReason};
// Reserved, not rendered in v1 — see Design decisions above:
// | { kind: 'needs_input' } | { kind: 'actioned' } | { kind: 'failed' }

export function deriveControlState(input: {
	hasEverRun: boolean;
	isRunning: boolean;
	outputRead: boolean;
	eligibility: EligibilityReason | null;
	atRunCap?: boolean;
	// A failed or cancelled outcome is not a new visible row state — a failure is meant to be
	// legible wherever the conversation renders, not summarised on the task row itself. It
	// resolves the control back to plain Action here; the outcome itself stays on RunState for
	// whatever eventually renders that conversation view. In practice the store never produces
	// hasEverRun: true alongside a failed/cancelled outcome (see novaInvocationStore.invoke), but
	// this function's own contract checks outcome explicitly rather than relying on that as an
	// unstated invariant callers must maintain.
	outcome?: RunOutcome | null;
	// Whether the resolved agent list has anything in it (novaInvocationStore.hasAgentToRun). A bare
	// Action click runs the first agent in that list, so with an empty one there is nothing to run
	// and the click does nothing at all — no state change, no notification. That is worse than a
	// stated disabled reason in every case, and in two of them it is not even momentary: an account
	// with the flag on and no granted agents, and a run-service blip that degrades the list to `[]`,
	// both leave a live-looking control permanently dead.
	//
	// Optional, defaulting to "there is one": absent information must not disable a control, and a
	// caller that does not model the agent list at all should get the behaviour it had before.
	hasAgent?: boolean;
	// Whether the task already has a conversation on the server, from Task.hasNovaConversation.
	// hasEverRun above is session state: it is true only for a run this browser tab started and is
	// gone on the next page load, so without this a task whose conversation is sitting right there
	// offers to start a fresh one every time it is reloaded. A discarded conversation is not one -
	// the field is false for a task whose newest thread was discarded, so discarding returns the
	// control to Action, which is what discarding is for.
	//
	// Optional, defaulting to "not known": a caller that does not select the field keeps exactly the
	// behaviour it had before rather than being told there is no conversation.
	hasConversation?: boolean;
}): ControlState {
	if (input.eligibility) return {kind: 'disabled', reason: input.eligibility};
	if (input.isRunning) return {kind: 'working'};
	// Checked ahead of atRunCap because the run cap's own copy tells the person to wait for a run to
	// finish — true only if a run would then be possible, which with no agent it would not be.
	const noAgentToRun = input.hasAgent === false;
	if (input.hasEverRun) {
		// The failed/cancelled override only applies here — a task that has genuinely never
		// succeeded falls through to the run-cap check below regardless of its outcome. It still
		// defers to the agent check: returning to Action with nothing to run would re-create the
		// dead click one run later.
		if (input.outcome && (input.outcome.kind === 'failed' || input.outcome.kind === 'cancelled')) {
			return noAgentToRun ? {kind: 'disabled', reason: 'no_agent_available'} : {kind: 'action'};
		}
		// A finished run stays readable whatever the list does afterwards — its answer exists, and
		// hiding it behind a disabled control because the list was re-fetched empty would lose it.
		return {kind: 'ready_or_continue', read: input.outputRead};
	}
	// Read as already seen rather than as a fresh result. Ready is how this control announces an
	// answer that arrived while the person was looking elsewhere; a conversation being loaded from
	// the server is one they have already met, so continuing it is the offer, not an unread badge.
	//
	// Below the run-cap and agent checks deliberately: continuing an existing conversation starts a
	// run like any other, so a person at the cap or with no agent to run must be told that here too
	// rather than being handed a control that cannot do anything.
	if (noAgentToRun) return {kind: 'disabled', reason: 'no_agent_available'};
	if (input.atRunCap) return {kind: 'disabled', reason: 'run_cap_reached'};
	if (input.hasConversation) return {kind: 'ready_or_continue', read: true};
	return {kind: 'action'};
}
